import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Badge,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';

interface LicenseField {
  label: string;
  value: string;
}

interface LicenseRequest {
  id: string;
  trackId: string;
  trackName: string;
  requestingOrgId: string;
  requestingOrgName: string;
  licenseType: string;
  dateExpires: number | null;
  dateStart: number | null;
  jobTitle?: string;
  client?: LicenseField | null;
  agency?: LicenseField | null;
  flightingPlatform?: LicenseField[];
  territories?: LicenseField[];
  notes?: string;
  createdAt: number;
  createdByName?: string;
  status: string;
}

interface PendingLicenseRequestsModalProps {
  firebase: any;
}

const PendingLicenseRequestsModal: React.FC<PendingLicenseRequestsModalProps> = ({ firebase }) => {
  const [requests, setRequests] = useState<LicenseRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  // Use notifications store for modal visibility and counts
  const isOpen = useNotificationsStore(state => state.showPendingRequestsModal);
  const setIsOpen = useNotificationsStore(state => state.setShowPendingRequestsModal);
  const setPendingRequestsCount = useNotificationsStore(state => state.setPendingRequestsCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Check for pending license requests when org changes
  useEffect(() => {
    const checkPendingRequests = async () => {
      if (!currentOrgId || !firebase) {
        setRequests([]);
        setPendingRequestsCount(0);
        return;
      }

      setLoading(true);
      try {
        const pendingRequests = await firebase.getPendingLicenseRequestsForOrg(currentOrgId);
        setRequests(pendingRequests);

        // Update count in notifications store
        setPendingRequestsCount(pendingRequests.length);

        // Auto-open on first load if there are pending requests (only once per org)
        if (!hasAutoOpened && pendingRequests.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      } catch (error) {
        console.error('Error checking pending license requests:', error);
      } finally {
        setLoading(false);
      }
    };

    checkPendingRequests();
  }, [currentOrgId, firebase, hasAutoOpened, setPendingRequestsCount, setIsOpen]);

  const handleApprove = async (request: LicenseRequest) => {
    if (!currentOrgId || !firebase) return;

    setProcessingIds((prev) => new Set(prev).add(request.id));

    try {
      const success = await firebase.approveLicenseRequest(currentOrgId, request.id);

      if (success) {
        // Remove from list and update count
        setRequests((prev) => {
          const updated = prev.filter((r) => r.id !== request.id);
          setPendingRequestsCount(updated.length);
          return updated;
        });
      }
    } catch (error) {
      console.error('Error approving license request:', error);
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(request.id);
        return newSet;
      });
    }
  };

  const handleReject = async (request: LicenseRequest) => {
    if (!currentOrgId || !firebase) return;

    setProcessingIds((prev) => new Set(prev).add(request.id));

    try {
      const success = await firebase.rejectLicenseRequest(currentOrgId, request.id);

      if (success) {
        // Remove from list and update count
        setRequests((prev) => {
          const updated = prev.filter((r) => r.id !== request.id);
          setPendingRequestsCount(updated.length);
          return updated;
        });
      }
    } catch (error) {
      console.error('Error rejecting license request:', error);
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(request.id);
        return newSet;
      });
    }
  };

  const handleSkip = () => {
    setIsOpen(false);
  };

  const toggleExpanded = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Close modal when all requests are processed
  useEffect(() => {
    if (isOpen && requests.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [requests.length, isOpen, loading, setIsOpen]);

  const formatLicenseType = (type: string) => {
    switch (type) {
      case 'blanket':
        return 'Blanket License';
      case '1year':
        return '1 Year';
      case '2year':
        return '2 Years';
      case '3year':
        return '3 Years';
      case 'custom':
        return 'Custom Duration';
      default:
        return type;
    }
  };

  const formatArrayField = (items: LicenseField[] | undefined) => {
    if (!items || items.length === 0) return '-';
    return items.map(item => item.label).join(', ');
  };

  if (!isOpen || requests.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleSkip}
      size="lg"
      style={{
        color: '#ffffff',
        maxWidth: '700px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleSkip}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleSkip}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          License Requests ({requests.length})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '500px',
          overflowY: 'auto',
        }}
      >
        <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)' }}>
          The following license requests have been submitted. Review and approve or reject each request.
        </p>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <div className="pending-requests-list">
            {requests.map((request) => (
              <div
                key={request.id}
                style={{
                  padding: '1rem',
                  marginBottom: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                {/* Main row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 500,
                        marginBottom: '0.25rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {request.trackName}
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      From: <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{request.requestingOrgName}</span>
                      {request.createdByName && (
                        <> ({request.createdByName})</>
                      )}
                      {' | '}
                      <Badge color="info" style={{ fontSize: '0.7rem' }}>{formatLicenseType(request.licenseType)}</Badge>
                      {request.dateExpires && request.licenseType !== 'blanket' && (
                        <> | Expires: {moment(request.dateExpires).format('DD/MM/YYYY')}</>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                    <Button
                      size="sm"
                      color="link"
                      onClick={() => toggleExpanded(request.id)}
                      style={{ color: 'rgba(255, 255, 255, 0.6)', padding: '0.25rem 0.5rem' }}
                    >
                      <i className={`tim-icons icon-minimal-${expandedId === request.id ? 'up' : 'down'}`}></i>
                    </Button>
                    <Button
                      size="sm"
                      className="btn-success-custom"
                      onClick={() => handleApprove(request)}
                      disabled={processingIds.has(request.id)}
                      style={{ minWidth: '70px' }}
                    >
                      {processingIds.has(request.id) ? (
                        <Spinner size="sm" />
                      ) : (
                        <span style={{ position: 'relative', zIndex: 2 }}>Approve</span>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      className="btn-danger-custom"
                      onClick={() => handleReject(request)}
                      disabled={processingIds.has(request.id)}
                      style={{ minWidth: '70px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Reject</span>
                    </Button>
                  </div>
                </div>

                {/* Expanded details */}
                {expandedId === request.id && (
                  <div
                    style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      fontSize: '0.85rem',
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      {request.jobTitle && (
                        <div>
                          <span className="text-muted">Job Title: </span>
                          <span>{request.jobTitle}</span>
                        </div>
                      )}
                      {request.client && (
                        <div>
                          <span className="text-muted">Brand: </span>
                          <span>{request.client.label}</span>
                        </div>
                      )}
                      {request.agency && (
                        <div>
                          <span className="text-muted">Agency: </span>
                          <span>{request.agency.label}</span>
                        </div>
                      )}
                      {request.flightingPlatform && request.flightingPlatform.length > 0 && (
                        <div>
                          <span className="text-muted">Platforms: </span>
                          <span>{formatArrayField(request.flightingPlatform)}</span>
                        </div>
                      )}
                      {request.territories && request.territories.length > 0 && (
                        <div>
                          <span className="text-muted">Territories: </span>
                          <span>{formatArrayField(request.territories)}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-muted">Requested: </span>
                        <span>{moment(request.createdAt).format('DD/MM/YYYY HH:mm')}</span>
                      </div>
                    </div>
                    {request.notes && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <span className="text-muted">Notes: </span>
                        <span style={{ fontStyle: 'italic' }}>{request.notes}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          className="btn-secondary-custom"
          onClick={handleSkip}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PendingLicenseRequestsModal;
